import { Link } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import HallsMap from "@/components/HallsMap";
import PostcodeLookup from "@/components/PostcodeLookup";
import Seo, { ORG_SCHEMA, breadcrumbSchema } from "@/components/Seo";
import Editable from "@/components/Editable";
import { MapPin, ArrowRight, Compass } from "@phosphor-icons/react";
import { REGIONS, HALLS, slug } from "@/lib/areas";

export default function ServiceAreas() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Sunshine Coast Service Areas | Puppl Puppy Training"
        description="Puppl services 45+ Sunshine Coast suburbs with private puppy consults, walk & trains and home check-ins. Group puppy school at three convenient halls."
        canonical={typeof window !== "undefined" ? window.location.origin + "/service-areas" : ""}
        schema={[
          ORG_SCHEMA,
          breadcrumbSchema([
            { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
            { name: "Service Areas", url: typeof window !== "undefined" ? window.location.origin + "/service-areas" : "/service-areas" },
          ]),
        ]}
      />
      <PublicHeader />

      <section className="relative bg-[#1b4a76] text-white overflow-hidden">
        <div className="absolute inset-0 pp-noise opacity-10" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <Editable id="service_areas.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-3 fade-up" defaultHtml="Where we train" />
            <Editable id="service_areas.heading" as="h1" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 fade-up fade-up-1" defaultHtml="Servicing the entire<br />Sunshine Coast." testid="areas-hero-title" />
            <Editable id="service_areas.subtitle" as="p" className="text-lg lg:text-xl opacity-90 leading-relaxed max-w-2xl fade-up fade-up-2" defaultHtml="From Coolum to Caloundra, the hinterland to the coast — the team travels across the Sunshine Coast for private puppy consults, walk &amp; trains and check-ins. Group puppy school runs from three convenient halls." />
            <div className="flex flex-wrap gap-3 mt-8 fade-up fade-up-3">
              <Link to="/services" className="btn-primary inline-flex items-center gap-2" data-testid="areas-cta-services">
                <Editable id="service_areas.cta1" inline as="span" defaultHtml="See Services" /> <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/register" className="bg-white/15 backdrop-blur-sm hover:bg-white/25 text-white border border-white/30 rounded-xl px-6 py-3 font-semibold transition-colors" data-testid="areas-cta-book">
                <Editable id="service_areas.cta2" inline as="span" defaultHtml="Book a Free Consult" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-2 fade-up fade-up-2">
            <PostcodeLookup />
          </div>
        </div>
      </section>

      {/* GROUP CLASS HALLS WITH MAPS */}
      <section className="py-20 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm uppercase tracking-wider font-semibold text-[#f38838] mb-3">Puppy school locations</p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-stone-900">3 convenient halls across the Coast</h2>
        </div>

        <div className="mb-10">
          <HallsMap />
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {Object.entries(HALLS).map(([key, l]) => {
            // Find the primary suburb for this hall (first suburb in the matching region)
            const region = REGIONS.find((r) => r.hall === key);
            const primarySlug = region ? slug(region.suburbs[0]) : "";
            return (
            <div key={key} className="card-pp p-6 flex flex-col" data-testid={`hall-${key}`}>
              <div className="w-10 h-10 rounded-lg bg-[#f38838]/15 flex items-center justify-center mb-3">
                <MapPin size={20} weight="duotone" className="text-[#f38838]" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-stone-900 mb-2">{l.name}</h3>
              <p className="text-sm text-stone-600 mb-3">{l.address}</p>
              <div className="text-xs uppercase tracking-wider font-semibold text-[#1b4a76] mb-4">{l.times}</div>
              {primarySlug && (
                <Link
                  to={`/service-areas/${primarySlug}`}
                  className="text-sm font-semibold text-[#1b4a76] hover:text-[#f38838] inline-flex items-center gap-1 mb-2"
                  data-testid={`hall-area-link-${key}`}
                >
                  Local services & info <ArrowRight size={14} weight="bold" />
                </Link>
              )}
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(l.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto text-sm font-semibold text-[#f38838] hover:text-[#d97324] inline-flex items-center gap-1"
                data-testid={`hall-directions-${key}`}
              >
                Get directions <ArrowRight size={14} weight="bold" />
              </a>
            </div>
            );
          })}
        </div>
      </section>

      {/* SUBURBS */}
      <section className="py-20 px-6 lg:px-12 bg-stone-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-wider font-semibold text-[#f38838] mb-3">In-home services</p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-stone-900">Private consults, walk &amp; trains and check-ins</h2>
            <p className="text-stone-600 mt-3 max-w-2xl mx-auto">the team travels to your home or favourite public spot across these suburbs. Click your suburb for tailored info.</p>
          </div>

          <div className="space-y-8">
            {REGIONS.map((r) => (
              <div key={r.name} className="card-pp p-6 lg:p-8" data-testid={`region-${slug(r.name)}`}>
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <div className="w-10 h-10 rounded-lg bg-[#1b4a76]/10 flex items-center justify-center">
                    <Compass size={22} weight="duotone" className="text-[#1b4a76]" />
                  </div>
                  <h3 className="font-heading font-semibold text-2xl text-stone-900">{r.name}</h3>
                  <span className="text-xs bg-stone-100 px-2.5 py-1 rounded-full font-semibold text-stone-600">{r.suburbs.length} suburbs</span>
                  <span className="ml-auto text-xs text-stone-500">Nearest hall: <span className="font-semibold text-[#1b4a76]">{HALLS[r.hall].name}</span></span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {r.suburbs.map((s) => (
                    <Link
                      key={s}
                      to={`/service-areas/${slug(s)}`}
                      className="flex items-center gap-2 text-sm text-stone-700 hover:text-[#f38838] px-3 py-2 rounded-lg hover:bg-white transition-colors group"
                      data-testid={`suburb-${slug(s)}`}
                    >
                      <MapPin size={14} weight="fill" className="text-[#f38838] flex-shrink-0" />
                      <span className="group-hover:underline">{s}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-stone-500 mt-10 max-w-2xl mx-auto">
            Don't see your suburb? <a href="mailto:support@puppl.app" className="text-[#f38838] font-semibold hover:underline">Email the team</a> — we may still cover your area.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto bg-[#f38838] rounded-3xl p-12 lg:p-16 text-white text-center relative overflow-hidden">
          <div className="absolute inset-0 pp-noise opacity-20" />
          <div className="relative">
            <Editable id="service_areas.final_cta.heading" as="h2" className="font-heading text-3xl lg:text-5xl font-bold mb-4" defaultHtml="Ready to start?" />
            <Editable id="service_areas.final_cta.body" as="p" className="text-lg opacity-90 mb-8 max-w-2xl mx-auto" defaultHtml="Create a free account, tell us about your puppy, and the team will be in touch." />
            <Link to="/services" className="bg-white text-[#f38838] hover:bg-[#FDFBF7] rounded-xl px-8 py-4 font-semibold inline-flex items-center gap-2 transition-colors" data-testid="areas-final-cta">
              <Editable id="service_areas.final_cta.button" inline as="span" defaultHtml="Explore our services" /> <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="hidden"></footer>
      <PublicFooter />

      <ChatWidget />
    </div>
  );
}
